// src/components/admin/ContentSubmissions.jsx
import React, { useState, useEffect } from 'react';
import './ContentSubmissions.css';

const CONTENT_LABELS = { in_progress: 'In Progress', submitted: 'Submitted', approved: 'Approved' };
const CONTENT_CLASSES = { in_progress: 'cs-pill-progress', submitted: 'cs-pill-submitted', approved: 'cs-pill-approved' };

const STORE_LABELS = {
  not_created: 'Not Created',
  waiting_for_content: 'Waiting for Content',
  in_progress: 'In Progress',
  ready_for_review: 'Ready for Review',
  created: 'Live',
};
const STORE_CLASSES = {
  not_created: 'cs-pill-gray',
  waiting_for_content: 'cs-pill-waiting',
  in_progress: 'cs-pill-progress',
  ready_for_review: 'cs-pill-submitted',
  created: 'cs-pill-approved',
};

const ContentSubmissions = () => {
  const [submissions, setSubmissions] = useState([]);
  const [loading, setLoading] = useState(true);

  const [selectedId, setSelectedId] = useState(null);
  const [detail, setDetail] = useState(null);
  const [checklist, setChecklist] = useState([]);
  const [detailLoading, setDetailLoading] = useState(false);

  useEffect(() => {
    fetchSubmissions();
  }, []);

  const fetchSubmissions = async () => {
    try {
      setLoading(true);
      const res = await fetch('https://creativepulse.pk/main-api/get-content-submissions.php');
      const data = await res.json();
      if (data.success) setSubmissions(data.submissions);
    } catch (e) {
      console.error('Error fetching submissions:', e);
    } finally {
      setLoading(false);
    }
  };

  const openSubmission = async (id) => {
    setSelectedId(id);
    setDetailLoading(true);
    setDetail(null);
    setChecklist([]);
    try {
      const res = await fetch(`https://creativepulse.pk/main-api/get-content-submission-detail.php?id=${id}`);
      const data = await res.json();
      if (data.success) {
        setDetail(data.submission);
        setChecklist(data.checklist);
      }
    } catch (e) {
      console.error('Error fetching detail:', e);
    } finally {
      setDetailLoading(false);
    }
  };

  const closeDrawer = () => {
    setSelectedId(null);
    setDetail(null);
    setChecklist([]);
  };

  const toggleChecklistItem = async (item) => {
    const newValue = !item.is_missing;
    setChecklist(prev => prev.map(c => c.id === item.id ? { ...c, is_missing: newValue } : c));
    try {
      await fetch('https://creativepulse.pk/main-api/update-checklist-item.php', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: item.id, is_missing: newValue })
      });
    } catch (e) {
      console.error('Failed to update checklist item:', e);
    }
  };

  const updateContentStatus = async (newStatus) => {
    setDetail(prev => ({ ...prev, content_status: newStatus }));
    try {
      await fetch('https://creativepulse.pk/main-api/update-content-submission.php', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: selectedId, content_status: newStatus })
      });
      fetchSubmissions();
    } catch (e) {
      console.error(e);
    }
  };

  const updateStoreStatus = async (newStatus) => {
    setDetail(prev => ({ ...prev, store_status: newStatus }));
    try {
      await fetch('https://creativepulse.pk/main-api/update-content-submission.php', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: selectedId, store_status: newStatus })
      });
      fetchSubmissions();
    } catch (e) {
      console.error(e);
    }
  };

  const formatSubmitted = (d) => {
    if (!d) return '—';
    const date = new Date(d);
    const today = new Date();
    const yesterday = new Date(today);
    yesterday.setDate(today.getDate() - 1);
    if (date.toDateString() === today.toDateString()) return 'Today';
    if (date.toDateString() === yesterday.toDateString()) return 'Yesterday';
    return date.toLocaleDateString();
  };

  return (
    <div className="content-submissions-page">
      <div className="cs-header">
        <h1>Content Submissions</h1>
        <p>Review client-submitted store information, products, images, and policies.</p>
      </div>

      {loading ? (
        <div className="cs-loading">Loading...</div>
      ) : submissions.length === 0 ? (
        <div className="cs-empty">No content submissions yet.</div>
      ) : (
        <div className="cs-table-container">
          <table className="cs-table">
            <thead>
              <tr>
                <th>Store</th>
                <th>Client</th>
                <th>Submitted</th>
                <th>Products</th>
                <th>Banners</th>
                <th>Policies</th>
                <th>Content</th>
                <th>Store</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {submissions.map(s => (
                <tr key={s.id}>
                  <td className="cs-store-name">{s.store_name || '—'}</td>
                  <td>{s.client_name}</td>
                  <td className="cs-muted">{formatSubmitted(s.submitted_at)}</td>
                  <td className="cs-muted">{s.products_count ?? '—'}</td>
                  <td className="cs-muted">{s.banners_count ?? '—'}</td>
                  <td className="cs-muted">{s.policies_count ?? '—'}</td>
                  <td><span className={`cs-pill ${CONTENT_CLASSES[s.content_status] || ''}`}>{CONTENT_LABELS[s.content_status] || s.content_status}</span></td>
                  <td><span className={`cs-pill ${STORE_CLASSES[s.store_status] || 'cs-pill-gray'}`}>{STORE_LABELS[s.store_status] || s.store_status}</span></td>
                  <td><button className="cs-view-btn" onClick={() => openSubmission(s.id)}>View Submission</button></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {selectedId && (
        <div className="cs-drawer-overlay">
          <div className="cs-drawer-backdrop" onClick={closeDrawer}></div>
          <div className="cs-drawer">
            {detailLoading || !detail ? (
              <div className="cs-drawer-loading">Loading...</div>
            ) : (
              <>
                <div className="cs-drawer-header">
                  <div>
                    <h2>{detail.store_name || detail.client_name}</h2>
                    <p className="cs-drawer-sub">{detail.client_name} · Submitted {formatSubmitted(detail.submitted_at)}</p>
                  </div>
                  <button className="cs-drawer-close" onClick={closeDrawer}>×</button>
                </div>

                <div className="cs-drawer-body">
                  <div className="cs-info-card">
                    <h3>Store Basics</h3>
                    <div className="cs-basics-row"><span className="cs-info-label">Tagline</span><span>{detail.tagline || '—'}</span></div>
                    <div className="cs-basics-row"><span className="cs-info-label">Description</span><span>{detail.description || '—'}</span></div>
                    <div className="cs-basics-row"><span className="cs-info-label">Announcement</span><span>{detail.announcement || '—'}</span></div>
                  </div>

                  <div className="cs-info-card">
                    <h3>Content Summary</h3>
                    <div className="cs-basics-row"><span className="cs-info-label">Products</span><span>{detail.products_count ?? '—'}</span></div>
                    <div className="cs-basics-row"><span className="cs-info-label">Banners</span><span>{detail.banners_count ?? '—'}</span></div>
                    <div className="cs-basics-row"><span className="cs-info-label">Policies</span><span>{detail.policies_count ?? '—'}</span></div>
                  </div>

                  <div className="cs-info-card">
                    <h3>Missing Information</h3>
                    {checklist.length === 0 ? (
                      <p className="cs-checklist-empty">Nothing tracked yet.</p>
                    ) : (
                      <ul className="cs-checklist">
                        {checklist.map(item => (
                          <li key={item.id}>
                            <label>
                              <input
                                type="checkbox"
                                checked={!item.is_missing}
                                onChange={() => toggleChecklistItem(item)}
                              />
                              <span className={item.is_missing ? 'cs-missing-text' : 'cs-resolved-text'}>{item.item_label}</span>
                            </label>
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>

                  <div className="cs-status-controls">
                    <div className="form-group">
                      <label>Content Status</label>
                      <select value={detail.content_status} onChange={(e) => updateContentStatus(e.target.value)}>
                        <option value="in_progress">In Progress</option>
                        <option value="submitted">Submitted</option>
                        <option value="approved">Approved</option>
                      </select>
                    </div>
                    <div className="form-group">
                      <label>Store Status</label>
                      <select value={detail.store_status} onChange={(e) => updateStoreStatus(e.target.value)}>
                        <option value="not_created">Not Created</option>
                        <option value="waiting_for_content">Waiting for Content</option>
                        <option value="in_progress">In Progress</option>
                        <option value="ready_for_review">Ready for Review</option>
                        <option value="created">Live</option>
                      </select>
                    </div>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default ContentSubmissions;